import { useEffect, useMemo, useState } from "react";
import { InputSheet } from "./components/InputSheet";
import { TextReview } from "./components/TextReview";
import { Reader } from "./components/Reader";
import { chunkText } from "./lib/chunker";
import {
  deriveTitle,
  listDocuments,
  loadDocument,
  makeDocumentId,
  saveDocument,
  type DocumentRecord,
} from "./lib/db";

type View =
  | { name: "home" }
  | { name: "sheet"; mode: "paste" | "upload" }
  | { name: "review"; text: string; sourceType: DocumentRecord["sourceType"] }
  | { name: "reader"; doc: DocumentRecord };

const DEFAULT_VOICE = "arcas" as const;
const DEFAULT_SPEED = 1 as const;

export default function App() {
  const [view, setView] = useState<View>({ name: "home" });
  const [recents, setRecents] = useState<DocumentRecord[]>([]);
  const [loadingRecents, setLoadingRecents] = useState(true);

  const refreshRecents = async () => {
    setLoadingRecents(true);
    const docs = await listDocuments();
    setRecents(docs);
    setLoadingRecents(false);
  };

  useEffect(() => {
    void refreshRecents();
  }, []);

  const mostRecent = recents[0];

  const openDocument = async (id: string) => {
    const doc = await loadDocument(id);
    if (doc) setView({ name: "reader", doc });
  };

  const handleTextSubmitted = (text: string, sourceType: DocumentRecord["sourceType"]) => {
    setView({ name: "review", text, sourceType });
  };

  const startReading = async (text: string, sourceType: DocumentRecord["sourceType"]) => {
    const doc: DocumentRecord = {
      id: makeDocumentId(),
      title: deriveTitle(text),
      text,
      sourceType,
      chunkIndex: 0,
      positionSec: 0,
      voice: DEFAULT_VOICE,
      speed: DEFAULT_SPEED,
      createdAt: Date.now(),
      lastOpened: Date.now(),
    };
    await saveDocument(doc);
    await refreshRecents();
    setView({ name: "reader", doc });
  };

  const persistPatch = (doc: DocumentRecord, patch: Partial<DocumentRecord>) => {
    const updated = { ...doc, ...patch };
    void saveDocument(updated);
  };

  const readerText = view.name === "reader" ? view.doc.text : "";
  const chunks = useMemoChunks(readerText);

  if (view.name === "reader") {
    return (
      <Reader
        doc={view.doc}
        chunks={chunks}
        onBack={() => {
          void refreshRecents();
          setView({ name: "home" });
        }}
        onPersist={(patch) => persistPatch(view.doc, patch)}
      />
    );
  }

  if (view.name === "review") {
    return (
      <TextReview
        initialText={view.text}
        onBack={() => setView({ name: "home" })}
        onStart={(text) => void startReading(text, view.sourceType)}
      />
    );
  }

  return (
    <div className="app">
      <header className="header">
        <div className="badge">🎧</div>
        <div>
          <h1>Josh Mainframe</h1>
          <p>AI reader for anything you paste or upload</p>
        </div>
      </header>

      {mostRecent && (
        <div className="resume-banner" onClick={() => void openDocument(mostRecent.id)}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <strong>Continue reading</strong>
            <span>{mostRecent.title}</span>
          </div>
          <span>▶</span>
        </div>
      )}

      <div className="action-grid">
        <button className="action-card" onClick={() => setView({ name: "sheet", mode: "paste" })}>
          <div className="icon">📋</div>
          <strong>Paste Text</strong>
          <span>From clipboard or notes</span>
        </button>
        <button className="action-card" onClick={() => setView({ name: "sheet", mode: "upload" })}>
          <div className="icon">📄</div>
          <strong>Upload File</strong>
          <span>TXT, PDF, DOCX</span>
        </button>
        <button className="action-card" disabled>
          <div className="icon">🔗</div>
          <strong>Open URL</strong>
          <span>Coming soon</span>
        </button>
      </div>

      <div className="section-title">
        <span>Recent items</span>
      </div>
      {loadingRecents && <p className="empty-state">Loading…</p>}
      {!loadingRecents && recents.length === 0 && (
        <p className="empty-state">Nothing yet. Paste some text or upload a file to get started.</p>
      )}
      {recents.map((doc) => (
        <div key={doc.id} className="recent-item" onClick={() => void openDocument(doc.id)}>
          <div className="icon">{sourceIcon(doc.sourceType)}</div>
          <div className="meta">
            <strong>{doc.title}</strong>
            <span>
              {doc.sourceType.toUpperCase()} · {new Date(doc.lastOpened).toLocaleDateString()}
            </span>
          </div>
        </div>
      ))}

      {view.name === "sheet" && (
        <InputSheet mode={view.mode} onClose={() => setView({ name: "home" })} onSubmit={handleTextSubmitted} />
      )}
    </div>
  );
}

function sourceIcon(sourceType: DocumentRecord["sourceType"]): string {
  switch (sourceType) {
    case "pdf":
      return "📕";
    case "docx":
      return "📘";
    case "image":
      return "🖼️";
    case "clipboard":
      return "📋";
    default:
      return "📝";
  }
}

const chunkCache = new Map<string, string[]>();
function useMemoChunks(text: string): string[] {
  return useMemo(() => {
    const cached = chunkCache.get(text);
    if (cached) return cached;
    const chunks = chunkText(text);
    chunkCache.set(text, chunks);
    return chunks;
  }, [text]);
}
